// The Compensation figures a seat reads (pure, no session or router), in the Hiring range card's sections (DC-230):
// each BRD chapter 6 row only when the field policy sent its key — the Hiring Manager's finalized version carries the
// recommendation line, the recommended range and the commission structure, and nothing of the grade, the internal range,
// the market data or the positioning ("Restricted fields must also be absent from search, exports, reports, email,
// attachments, audit summaries and APIs."). A section with no row the seat may read is not drawn.

import type { GradeOption, VersionOut } from "@/api/tools";
import { KeyValue, type KeyValueItem } from "@/mc";
import { range } from "../requisition/data";
import { COMP_ROW_LABELS } from "../requisition/labels";
import { COMP_FIELD_LABELS, HIRING_RANGE_LABELS, internalRangeLine } from "./labels";

const POSITIONING_WORD: Readonly<Record<string, string>> = {
  below_mid: HIRING_RANGE_LABELS.belowMid, at_mid: HIRING_RANGE_LABELS.atMid, above_mid: HIRING_RANGE_LABELS.aboveMid,
};

const whole = (value: string | number | null | undefined): string => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== "" && Number.isFinite(n) ? n.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—";
};

export interface ReadSection {
  title: string;
  items: KeyValueItem[];
}

/** The figures a seat reads, by the worksheet's five sections; each row only when the field policy sent its key. */
export function readSections(version: VersionOut | null, grades: readonly GradeOption[] = []): ReadSection[] {
  if (!version) return [];
  const has = (key: keyof VersionOut) => key in version;
  const grade = grades.find((option) => option.key === version.salary_grade);
  const out: ReadSection[] = [];
  const section = (title: string, items: KeyValueItem[]) => { if (items.length) out.push({ title, items }); };

  const gradeRows: KeyValueItem[] = [];
  if (has("salary_grade")) gradeRows.push([COMP_ROW_LABELS.salaryGrade, grade?.label ?? (version.salary_grade || "—")]);
  if (has("internal_range_min") || has("internal_range_max")) {
    gradeRows.push([
      COMP_FIELD_LABELS.internalRange,
      grade
        ? internalRangeLine({ min: whole(grade.min), max: whole(grade.max), currency: grade.currency, mid: whole(grade.mid) })
        : range(version.internal_range_min, version.internal_range_max, version.currency),
    ]);
  }
  section(HIRING_RANGE_LABELS.grade, gradeRows);

  const marketRows: KeyValueItem[] = [];
  if (has("market_data")) {
    const data = version.market_data;
    marketRows.push(data && "market_mid" in data
      ? [HIRING_RANGE_LABELS.marketMid, `${whole(data["market_mid"] as string | number)} ${version.currency ?? ""}`.trim()]
      : [COMP_FIELD_LABELS.marketData, data ? <MarketData data={data} /> : "—"]);
  }
  section(HIRING_RANGE_LABELS.marketData, marketRows);

  const recoRows: KeyValueItem[] = [];
  if (has("recommendation")) recoRows.push([COMP_FIELD_LABELS.recommendation, version.recommendation || "—"]);
  if (has("recommended_range_min") || has("recommended_range_max")) {
    recoRows.push([HIRING_RANGE_LABELS.recommendedHiringRange, range(version.recommended_range_min, version.recommended_range_max, version.currency)]);
  }
  if (has("positioning_level")) recoRows.push([COMP_FIELD_LABELS.positioning, POSITIONING_WORD[version.positioning_level ?? ""] ?? "—"]);
  if (has("positioning")) recoRows.push([HIRING_RANGE_LABELS.positioningNote, version.positioning || "—"]);
  section(HIRING_RANGE_LABELS.recommendation, recoRows);

  const bonusRows: KeyValueItem[] = [];
  if (has("bonus_target_pct")) bonusRows.push([HIRING_RANGE_LABELS.bonusTargetPct, version.bonus_target_pct ? `${Number(version.bonus_target_pct)}%` : "—"]);
  if (has("has_commission")) bonusRows.push([HIRING_RANGE_LABELS.commission, version.has_commission ? version.commission_structure || "—" : HIRING_RANGE_LABELS.none]);
  else if (has("commission_structure")) bonusRows.push([COMP_FIELD_LABELS.commissionStructure, version.commission_structure || "—"]);
  section(HIRING_RANGE_LABELS.bonusCommission, bonusRows);

  const noteRows: KeyValueItem[] = [];
  if (has("working_notes")) noteRows.push([COMP_FIELD_LABELS.workingNotes, version.working_notes || "—"]);
  if (has("approval_evidence")) noteRows.push([COMP_FIELD_LABELS.approvalEvidence, version.approval_evidence || "—"]);
  section(HIRING_RANGE_LABELS.notesEvidence, noteRows);
  return out;
}

/** The figures a seat reads as one list of rows, in the sections' order. */
export function readItems(version: VersionOut | null, grades: readonly GradeOption[] = []): KeyValueItem[] {
  return readSections(version, grades).flatMap((section) => section.items);
}

export function MarketData({ data }: { data: Record<string, unknown> }) {
  return <KeyValue rows items={Object.entries(data).map(([key, value]) => [key, typeof value === "object" ? JSON.stringify(value) : String(value)])} />;
}
